import { useState } from 'react'
import { Pressable, StyleSheet, Text, View } from 'react-native'
import { formatGBP } from '../format'
import { colors, fontFamily, fontSize, radius, space } from '../tokens'
import { BandPillRow } from './BandPillRow'

type BandSuggestion = {
  band: string
  source: 'user-sale' | 'postcode-median'
  backcastValueGbp: number
}

type Props = {
  postcode: string
  houseNumber: string
  reason: 'scotland' | 'no-coverage'
  validBands: readonly string[]
  bandSuggestion?: BandSuggestion
  chargeByBand?: Record<string, number>
  localAuthority?: string
  onSubmit: (band: string) => void
  isPending: boolean
  errorMessage?: string
}

const HEADLINE: Record<Props['reason'], string> = {
  scotland: "We can't auto-look-up Scottish bands yet",
  'no-coverage': "We couldn't find your specific property in our band database",
}

const SUBLINE: Record<Props['reason'], string> = {
  scotland:
    "Your band lives on the Scottish Assessors website, which we don't connect to yet. Pop over to saa.gov.uk to look up your band, then enter it below — we'll do the rest.",
  'no-coverage':
    "It's an edge case (sometimes happens with new builds or unusual addresses). Tell us your current band — you'll find it on your latest council tax bill — and we'll run the comparison.",
}

const SUGGESTION_LABEL: Record<BandSuggestion['source'], (gbp: number) => string> = {
  'user-sale': (gbp) =>
    `Best guess: Band based on your last sale price (1991 backcast ≈ ${formatGBP(gbp)}). Tap to override if your bill says otherwise.`,
  'postcode-median': (gbp) =>
    `Best guess: Band based on your postcode's median sale price (1991 backcast ≈ ${formatGBP(gbp)}). Tap to override if your bill says otherwise.`,
}

export const BandRequiredForm = ({
  postcode,
  houseNumber,
  reason,
  validBands,
  bandSuggestion,
  chargeByBand,
  localAuthority,
  onSubmit,
  isPending,
  errorMessage,
}: Props) => {
  const [selected, setSelected] = useState<string | null>(bandSuggestion?.band ?? null)
  const canSubmit = selected !== null && !isPending

  return (
    <View style={styles.container}>
      <Text style={styles.title}>{HEADLINE[reason]}</Text>
      <Text style={styles.subtitle}>{SUBLINE[reason]}</Text>

      <View style={styles.addressBlock}>
        <Text style={styles.addressLabel}>Property</Text>
        <Text style={styles.addressValue}>
          {houseNumber}, {postcode}
        </Text>
        {localAuthority ? (
          <Text style={styles.addressMeta}>Local authority: {localAuthority}</Text>
        ) : null}
      </View>

      {bandSuggestion ? (
        <Text style={styles.suggestion}>
          {SUGGESTION_LABEL[bandSuggestion.source](bandSuggestion.backcastValueGbp)}
        </Text>
      ) : null}

      <Text style={styles.label}>Your current council tax band</Text>
      <BandPillRow
        validBands={validBands}
        selected={selected}
        chargeByBand={chargeByBand}
        disabled={isPending}
        onSelect={setSelected}
      />

      <Pressable
        accessibilityRole="button"
        disabled={!canSubmit}
        onPress={() => selected && onSubmit(selected)}
        style={[styles.button, !canSubmit && styles.buttonDisabled]}
      >
        <Text style={styles.buttonText}>{isPending ? 'Running check…' : 'Run band check'}</Text>
      </Pressable>

      {errorMessage ? <Text style={styles.error}>{errorMessage}</Text> : null}
    </View>
  )
}

const styles = StyleSheet.create({
  container: { width: '100%', maxWidth: 480, gap: space[16] },
  title: {
    fontSize: fontSize.h2,
    fontWeight: '600',
    color: colors.primary[900],
    fontFamily: fontFamily.body,
    letterSpacing: -0.2,
  },
  subtitle: {
    fontSize: fontSize.body,
    color: colors.gray[700],
    lineHeight: 24,
    fontFamily: fontFamily.body,
  },
  addressBlock: {
    backgroundColor: colors.gray[50],
    borderWidth: 1,
    borderColor: colors.gray[300],
    paddingHorizontal: space[16],
    paddingVertical: 12,
    borderRadius: radius.sm,
    gap: 4,
  },
  addressLabel: {
    fontSize: fontSize.tiny,
    color: colors.gray[500],
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    fontFamily: fontFamily.body,
  },
  addressValue: {
    fontSize: fontSize.body,
    color: colors.gray[900],
    fontFamily: fontFamily.body,
  },
  addressMeta: {
    fontSize: fontSize.small,
    color: colors.gray[700],
    fontFamily: fontFamily.body,
  },
  suggestion: {
    fontSize: fontSize.small,
    color: colors.primary[900],
    backgroundColor: colors.primary[100],
    borderLeftWidth: 4,
    borderLeftColor: colors.primary[700],
    borderRadius: radius.sm,
    paddingHorizontal: 12,
    paddingVertical: 10,
    lineHeight: 20,
    fontFamily: fontFamily.body,
  },
  label: {
    fontSize: fontSize.body,
    fontWeight: '500',
    color: colors.gray[700],
    fontFamily: fontFamily.body,
  },
  button: {
    backgroundColor: colors.primary[700],
    paddingVertical: 14,
    borderRadius: radius.sm,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 48,
    marginTop: space[8],
  },
  buttonDisabled: { backgroundColor: colors.gray[300] },
  buttonText: {
    color: 'white',
    fontSize: fontSize.h4,
    fontWeight: '500',
    fontFamily: fontFamily.body,
  },
  error: {
    color: colors.error[900],
    backgroundColor: colors.error[100],
    borderLeftWidth: 4,
    borderLeftColor: colors.error[700],
    padding: space[8],
    paddingLeft: 12,
    borderRadius: radius.sm,
    marginTop: space[8],
    fontFamily: fontFamily.body,
    fontSize: fontSize.small,
  },
})
